"use client"

import { useSession, SessionProvider } from "next-auth/react"
import { useEffect } from "react"
import { useRouter, usePathname } from "next/navigation"
import { ProfileProvider } from "@/components/ProfileProvider"
import { UrlLinks } from "@/shared/config"
import { Toaster } from "@/components/ui/sonner"
import { installAuthFetchInterceptor } from "@/lib/session/authFetchInterceptor"
import { SessionExpiryWarning } from "@/components/SessionExpiryWarning"
import { isCapacitor } from "@/lib/mobile/platform"


function SessionWatcher() {
  const { status } = useSession()
  const router = useRouter()
  const pathname = usePathname()

  useEffect(() => {
    // /mobile-entry intentionally runs unauthenticated — it establishes the session
    // itself via the Capacitor PKCE flow. Redirecting it to /login races that flow
    // and forces the device down the web OIDC path (wrong client, wrong redirect_uri).
    if (
      status === "unauthenticated" &&
      !pathname.startsWith(UrlLinks.pages.login) &&
      !pathname.startsWith(UrlLinks.pages.mobileEntry)
    ) {
      // The Capacitor app signs in through its own PKCE flow, not the web OIDC client.
      if (isCapacitor()) {
        router.push(UrlLinks.pages.mobileEntry)
        return
      }
      router.push(
        `/login?reason=session_expired&callbackUrl=${encodeURIComponent(pathname)}`
      )
    }
  }, [status, router, pathname])

  // No session polling (it kept waking Cloud Run on idle devices, and /api/auth/session skips the
  // middleware, so it never noticed an ended session). Instead, when the user comes back to the
  // tab, ask the middleware: an ended session answers 401 + x-session-end and the fetch
  // interceptor sends the user to login. The check is never counted as activity.
  useEffect(() => {
    if (status !== "authenticated") return
    let lastCheckAt = 0
    const check = () => {
      if (document.visibilityState !== "visible") return
      const now = Date.now()
      if (now - lastCheckAt < SESSION_CHECK_MIN_GAP_MS) return // focus + visibilitychange fire together
      lastCheckAt = now
      fetch(UrlLinks.api.sessionCheck, { cache: "no-store", credentials: "same-origin" }).catch(() => {
        // offline: the next request will tell
      })
    }
    window.addEventListener("focus", check)
    document.addEventListener("visibilitychange", check)
    return () => {
      window.removeEventListener("focus", check)
      document.removeEventListener("visibilitychange", check)
    }
  }, [status])

  return null
}

/** At most one session check per this gap (window focus and visibilitychange often fire together). */
const SESSION_CHECK_MIN_GAP_MS = 5_000

export function Providers({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    installAuthFetchInterceptor()
  }, [])

  return (
    // No polling and no focus refetch: SessionWatcher's /api/session/check on focus replaces both.
    <SessionProvider refetchInterval={0} refetchOnWindowFocus={false}>
      <SessionWatcher />
      <SessionExpiryWarning />
      <ProfileProvider>{children}</ProfileProvider>
      <Toaster />
    </SessionProvider>
  )
}
